import {
    FaShieldAlt,
    FaMapMarkerAlt,
    FaMapSigns,
    FaSearch,
    FaDraftingCompass,
    FaBriefcase,
    FaLeaf,
    FaFingerprint,
    FaDatabase,
    FaSyncAlt,
    FaClipboardCheck,
    FaClock,
    FaUserCheck,
    FaBell,
    FaFileSignature,
    FaBan,
    FaGlobeAfrica,
    FaCertificate,
    FaEye,
    FaUsers,
    FaLock,
    FaShareAlt,
    FaHandshake,
    FaProjectDiagram,
    FaAward,
    FaSeedling,
    FaChartLine,
    FaUniversity,
    FaPlane,
} from "react-icons/fa";

import iconSolC2pa from "../../assets/icon-sol-c2pa.svg";
import iconSolGeofence from "../../assets/icon-sol-geofence.svg";
import iconSolMilestone from "../../assets/icon-sol-milestone.svg";
import iconSolSearch from "../../assets/icon-sol-search.svg";
import iconSolPortfolio from "../../assets/icon-sol-portfolio.svg";
import iconSolGreen from "../../assets/icon-sol-green.svg";
import iconSolBorderless from "../../assets/icon-sol-borderless.svg";

import solC2paHero from "../../assets/sol-c2pa-hero.svg";
import solC2paDetail from "../../assets/sol-c2pa-detail.svg";
import solC2paCurl from "../../assets/sol-c2pa-curl.svg";
import solC2paIcAuthenticity from "../../assets/sol-c2pa-ic-authenticity.svg";
import solC2paIcTamper from "../../assets/sol-c2pa-ic-tamper.svg";
import solC2paIcTrust from "../../assets/sol-c2pa-ic-trust.svg";
import solC2paIcAudit from "../../assets/sol-c2pa-ic-audit.svg";
import solC2paCheckSigned from "../../assets/sol-c2pa-check-signed.svg";
import solC2paCheckDetails from "../../assets/sol-c2pa-check-details.svg";
import solC2paCheckPlatforms from "../../assets/sol-c2pa-check-platforms.svg";
import solC2paCheckVerify from "../../assets/sol-c2pa-check-verify.svg";

import solGeoHero from "../../assets/sol-geo-hero.svg";
import solGeoMilestones from "../../assets/sol-geo-milestones.svg";
import solGeoPhoto from "../../assets/sol-geo-photo.svg";
import solGeoIcEvidence from "../../assets/sol-geo-ic-evidence.svg";
import solGeoIcTimestamps from "../../assets/sol-geo-ic-timestamps.svg";
import solGeoIcAccountability from "../../assets/sol-geo-ic-accountability.svg";
import solGeoIcVisibility from "../../assets/sol-geo-ic-visibility.svg";

import solTeHero from "../../assets/sol-te-hero.svg";
import solTeBadge from "../../assets/sol-te-badge.svg";
import solTePhones from "../../assets/sol-te-phones.svg";

import solSearchHero from "../../assets/sol-search-hero.svg";
import solSearchDetail from "../../assets/sol-search-detail.svg";
import solSearchIcDiscovery from "../../assets/sol-search-ic-discovery.svg";
import solSearchIcAuthenticity from "../../assets/sol-search-ic-authenticity.svg";
import solSearchIcVisibility from "../../assets/sol-search-ic-visibility.svg";
import solSearchIcNetwork from "../../assets/sol-search-ic-network.svg";
import solSearchIcSecure from "../../assets/sol-search-ic-secure.svg";

import solVdpHero from "../../assets/sol-vdp-hero.svg";
import solVdpDetail from "../../assets/sol-vdp-detail.svg";

/**
 * Central config for every Solutions page.
 * Each entry drives both the header mega-menu and the /solutions/:slug view.
 * `menuIconSrc` (svg asset) is used in the mega-menu; `menuIcon` (react-icon)
 * remains as a fallback for entries without a dedicated asset.
 */
export const SOLUTIONS = [
    {
        slug: "c2pa-certified-media",
        menuIconSrc: iconSolC2pa,
        menuLabel: "C2PA-Certified Site Media",
        menuDesc: "Prove every capture.",
        menuIcon: FaShieldAlt,
        overtitle: "C2PA Certified Media",
        title: "Every Construction Photo Carries Proof of",
        titleHighlight: "Authenticity",
        subtitle:
            "ConRa helps construction teams manage tasks, track progress, capture site updates and deliver projects on time and on budget.",
        heroImage: solC2paHero,
        heroDecor: solC2paCurl,
        benefitsIntro:
            "In a world where images can be edited or generated by AI, ConRa gives you undeniable proof that your project updates are real and trustworthy.",
        benefits: [
            { icon: FaFingerprint, iconSrc: solC2paIcAuthenticity, tile: "blue", title: "Prove Authenticity", desc: "Cryptographic proof shows your media is genuine." },
            { icon: FaDatabase, iconSrc: solC2paIcTamper, tile: "gold", title: "AI-Tamper Protection", desc: "Detect AI generated or manipulated content with global standards." },
            { icon: FaSyncAlt, iconSrc: solC2paIcTrust, tile: "gold", title: "Build Trust", desc: "Strengthen confidence with clients, investors, lenders and regulators." },
            { icon: FaClipboardCheck, iconSrc: solC2paIcAudit, tile: "blue", title: "Audit & Compliance", desc: "Tamper proof evidence ready for audits, claims and compliance." },
        ],
        detail: {
            image: solC2paDetail,
            title: "Instantly Verifiable,",
            titleHighlight: "Independently Trusted",
            paragraph:
                "Every certified photo or video includes a digital credential that anyone can verify, ensuring full transparency across your project.",
            checklist: [
                { iconSrc: solC2paCheckSigned, title: "Cryptographically signed with C2PA" },
                { iconSrc: solC2paCheckDetails, title: "Includes location, timestamp and capture details" },
                { iconSrc: solC2paCheckPlatforms, title: "Works across platforms and devices" },
                { iconSrc: solC2paCheckVerify, title: "Verifiable by anyone, anywhere" },
            ],
        },
    },
    {
        slug: "geofenced-timestamped-audits",
        menuIconSrc: iconSolGeofence,
        menuLabel: "Tight Geofenced & Timestamped Audits",
        menuDesc: "Verify every location.",
        menuIcon: FaMapMarkerAlt,
        overtitle: "Tight Geofenced & Timestamped Audits",
        title: "The Right Proof. From the Right Place.",
        titleHighlight: "At the Right Time",
        subtitle:
            "ConRa locks every site photo, document, and audit to an exact GPS location and trusted timestamp so your construction evidence is real, verifiable and dispute proof.",
        heroImage: solGeoHero,
        heroLayout: "split",
        benefitsIntro:
            "Accurate location and time verification eliminates disputes, prevents fraud and ensures accountability across every project stage.",
        benefits: [
            { icon: FaClipboardCheck, iconSrc: solGeoIcEvidence, tile: "blue", title: "Verified On-Site Evidence", desc: "Ensures every photo and document is captured at the project site." },
            { icon: FaClock, iconSrc: solGeoIcTimestamps, tile: "gold", title: "Trusted Timestamps", desc: "Every capture is locked with a trusted timestamp, ensuring a reliable audit trail of site activity." },
            { icon: FaUserCheck, iconSrc: solGeoIcAccountability, tile: "gold", title: "Improve Team Accountability", desc: "Field teams, consultants and contractors stay accountable with verifiable proof of work done." },
            { icon: FaBell, iconSrc: solGeoIcVisibility, tile: "blue", title: "Better Project Visibility", desc: "Managers track real time activity across locations with accurate, data backed reports." },
            { icon: FaFileSignature, iconSrc: solGeoIcEvidence, tile: "blue", title: "Funding & Compliance Ready", desc: "Banks, investors and regulators can verify progress confidently before approving payments or milestones." },
            { icon: FaBan, iconSrc: solGeoIcTimestamps, tile: "gold", title: "Prevents Disputes & Fraud", desc: "Eliminate false claims, backdating and location manipulation with immutable geo-time data." },
        ],
        detail: {
            title: "Designed for Every Stage of",
            titleHighlight: "Construction",
            paragraph:
                "From excavation to handover, every update is geofenced and timestamped, giving a clear, verifiable record of how the project is built.",
            cards: [
                {
                    tint: "blue",
                    title: "Daily Progress Photos",
                    desc: "Automatically capture geofenced, timestamped photos that prove where and when work was completed. Every image carries trusted authenticity information making progress reporting simple and reliable.",
                    image: solGeoPhoto,
                    imageLabel: "C2PA - Certified",
                },
                {
                    tint: "orange",
                    title: "Milestones Approvals",
                    desc: "Secure every site photo and video the moment it is captured. As an active member of the Coalition for Content Provenance and Authenticity (C2PA), we use the same global security standards. Eliminate guesswork and fraudulent reporting with automated, location-locked progress tracking.",
                    image: solGeoMilestones,
                },
                {
                    tint: "yellow",
                    title: "Site Reports and Observations",
                    desc: "Move away from easily edited spreadsheets and unverified site reports. We build a permanent, chronological history of your project that can never be altered. Every structural phase and milestone sign-off is cryptographically sealed into a tamper-proof site audit trail — completely transparent site reporting where past records can never be backdated, deleted, or covered up.",
                    image: solGeoMilestones,
                    wide: true,
                },
            ],
        },
    },
    {
        slug: "tamper-evident-timelines",
        menuIconSrc: iconSolMilestone,
        menuLabel: "Tamper-Evident Progress Timelines",
        menuDesc: "Every milestone preserved.",
        menuIcon: FaMapSigns,
        overtitle: "Tamper-Evident Progress Timelines",
        title: "A Timeline That Can Never Be",
        titleHighlight: "Altered",
        subtitle:
            "Move away from easily edited spreadsheets and unverified site reports. ConRa builds a permanent history of your project.",
        heroImage: solTeHero,
        heroImageMaxWidth: 717,
        heroBadge: solTeBadge,
        benefitsIntro:
            "In a world where records can be edited or backdated, ConRa gives you a project history everyone can rely on.",
        benefits: [
            { icon: FaFingerprint, title: "Immutable Audit Trails", desc: "Every update is sealed and cannot be changed, deleted or backdated." },
            { icon: FaDatabase, title: "Audit Ready", desc: "Provide external auditors with an unquestionable project trail." },
            { icon: FaSyncAlt, title: "Transparency", desc: "All project stakeholders see the same verified project history." },
            { icon: FaClipboardCheck, title: "Insurance Confidence", desc: "Insurers rely on verified timelines for risk assessment." },
        ],
        detail: {
            image: solTePhones,
            overtitle: "One Project, Unalterable History",
            title: "See Every",
            titleHighlight: "Milestone",
            paragraph:
                "We build a permanent, chronological history of your project that can never be altered. Every structural phase and milestone sign-off is cryptographically sealed into a tamper-proof site audit trail — external auditors, insurance firms, and property owners can confidently rely on it, and past records can never be backdated, deleted, or covered up.",
            checklist: [
                { title: "Time & Date Stamped" },
                { title: "GPS location verified" },
                { title: "C2PA certified media attached" },
            ],
        },
    },
    {
        slug: "search-verification-engine",
        menuIconSrc: iconSolSearch,
        menuLabel: "ConRa Search Verification Engine",
        menuDesc: "Search, verify and connect.",
        menuIcon: FaSearch,
        overtitle: "ConRa Search Verification Engine",
        title: "The Search Engine for the",
        titleHighlight: "Built Environment",
        subtitle:
            "Every published project becomes searchable, verifiable and trusted by clients, financial institutions, investors and industry partners worldwide.",
        heroImage: solSearchHero,
        benefitsIntro:
            "A centralized search engine acting as a global discovery and verification portal for the built environment.",
        benefits: [
            { icon: FaGlobeAfrica, iconSrc: solSearchIcDiscovery, tile: "blue", title: "Global Project Discovery", desc: "Find verified projects across countries, sectors and stages." },
            { icon: FaCertificate, iconSrc: solSearchIcAuthenticity, tile: "gold", title: "Verified Authenticity", desc: "Every project and company is backed by tamper proof evidence." },
            { icon: FaEye, iconSrc: solSearchIcVisibility, tile: "gold", title: "Real Progress Visibility", desc: "See real time progress with photo, location, time and milestone date." },
            { icon: FaUsers, iconSrc: solSearchIcNetwork, tile: "blue", title: "Trusted Partner Network", desc: "Connect with verified contractors, consultants and more." },
            { icon: FaLock, iconSrc: solSearchIcSecure, tile: "blue", title: "Secure & Transparent", desc: "Immutable records ensure accountability and build trust for all." },
        ],
        detail: {
            image: solSearchDetail,
            title: "A Global Discovery and Verification Portal for the",
            titleHighlight: "Built Environment",
            paragraph:
                "This centralized search engine acts as a global discovery and verification portal for the built environment.",
            checklist: [
                { title: "Verify Developer Portfolios", desc: "Browse trusted companies and validate their completed and ongoing projects." },
                { title: "Remote Funding Verification", desc: "Enable banks, SACCOs, donors, and lenders to verify project progress before releasing funds." },
                { title: "Global Partner Discovery", desc: "Find vetted contractors, consultants, and joint venture partners with proven track records." },
                { title: "Green Building Directory", desc: "Discover sustainable and environmentally responsible construction projects." },
            ],
        },
    },
    {
        slug: "borderless-architecture-engineering",
        menuIconSrc: iconSolBorderless,
        menuLabel: "Borderless Architecture & Engineering",
        menuDesc: "Engineer without limit.",
        menuIcon: FaDraftingCompass,
        overtitle: "Borderless Architecture & Engineering",
        title: "World-Class Expertise.",
        titleHighlight: "Without Borders",
        subtitle:
            "ConRa connects architects, engineers and construction professionals across borders, so distance never limits who designs, reviews or delivers your project.",
        benefitsIntro:
            "Verified credentials and trusted project records make cross-border collaboration safe, transparent and productive.",
        benefits: [
            { icon: FaGlobeAfrica, title: "Global Talent Access", desc: "Engage verified architects and engineers from anywhere in the world." },
            { icon: FaCertificate, title: "Verified Credentials", desc: "Every professional profile is backed by a verifiable track record of completed work." },
            { icon: FaProjectDiagram, title: "Remote Collaboration", desc: "Review plans, milestones and site evidence in real time — no site visit required." },
            { icon: FaClipboardCheck, title: "Cross-Border Compliance", desc: "Trusted documentation supports approvals, permits and standards across jurisdictions." },
        ],
        detail: {
            title: "Design and Deliver from",
            titleHighlight: "Anywhere",
            paragraph:
                "Verified site media, geofenced audits and immutable timelines give remote teams the same confidence as being on site — enabling true borderless project delivery.",
            checklist: [
                { title: "Remote design reviews backed by verified site evidence" },
                { title: "Trusted progress data for distributed teams" },
                { title: "Verifiable professional portfolios" },
                { title: "Seamless collaboration across time zones" },
            ],
        },
    },
    {
        slug: "verified-digital-portfolio",
        menuIconSrc: iconSolPortfolio,
        menuLabel: "Verified Digital Portfolio",
        menuDesc: "Showcase proven excellence.",
        menuIcon: FaBriefcase,
        overtitle: "Verified Digital Portfolio",
        title: "Your Work. Verified. Visible.",
        titleHighlight: "Valued Everywhere.",
        subtitle:
            "ConRa turns every published project into a verified digital portfolio with a unique project code. Share your work globally, anywhere and let your track record speak for you.",
        heroImage: solVdpHero,
        benefitsIntro:
            "In a world where images can be edited or generated by AI, ConRa gives you undeniable proof that your project updates are real and trustworthy.",
        benefits: [
            { icon: FaFingerprint, title: "Instant Trust & Credibility", desc: "Build confidence with clients, investors and partners through verified project records." },
            { icon: FaDatabase, title: "Global Visibility", desc: "Get discovered by clients and investors searching for proven construction partners." },
            { icon: FaShareAlt, title: "Easy to Share", desc: "Share your unique project code or link anywhere." },
            { icon: FaSyncAlt, title: "Always Up-to-Date", desc: "Your portfolio updates automatically as you publish new projects and milestones." },
            { icon: FaLock, title: "Tamper-Proof & Secure", desc: "All project data is immutable and backed by C2PA verified site evidence." },
        ],
        detail: {
            image: solVdpDetail,
            imagePosition: "left",
            overtitle: "Built for Construction. Designed for Results",
            title: "Share Your Portfolio.",
            titleHighlight: "Open Doors.",
            paragraph:
                "Your unique code gives anyone instant access to your verified company profile and all your published projects.",
            checklist: [
                { title: "Verified & Tamper-Proof" },
                { title: "Lasting Proof of Experience" },
                { title: "Share Anywhere" },
                { title: "Always Up to Date" },
            ],
        },
    },
    {
        slug: "ngos",
        menuIcon: FaHandshake,
        menuLabel: "NGOs & Diaspora Investors",
        menuDesc: "From distance-based anxiety to verifiable certainty.",
    },
    {
        slug: "contractors",
        menuIcon: FaProjectDiagram,
        menuLabel: "Contractors",
        menuDesc: "Build your verified track record.",
        overtitle: "For Contractors",
        title: "Build Your Verified",
        titleHighlight: "Track Record",
        subtitle: "ConRa helps contractors build a tamper-proof digital portfolio that showcases their expertise, earns client trust and wins more projects — backed by verified evidence of every job completed.",
        benefitsIntro: "Verified project records turn your past work into a powerful business development tool.",
        benefits: [
            { icon: FaCertificate, title: "Verified Credentials", desc: "Showcase a tamper-proof portfolio of completed projects clients and investors can trust." },
            { icon: FaProjectDiagram, title: "Win More Work", desc: "Stand out to developers and investors with verifiable evidence of quality workmanship." },
            { icon: FaHandshake, title: "Trusted by Clients", desc: "Clients verify your track record before signing any contract — no more relying on referrals alone." },
            { icon: FaChartLine, title: "Grow Your Reputation", desc: "Every milestone adds to a permanent, growing professional record that outlasts any CV." },
        ],
        detail: {
            title: "Your Reputation,",
            titleHighlight: "Permanently On Record",
            paragraph: "Every project you complete becomes a verified entry in your digital portfolio — backed by geo-tagged photos, timestamped milestones and cryptographic proof that the work was done.",
            checklist: [
                { title: "Tamper-proof project evidence" },
                { title: "Verified milestones clients trust" },
                { title: "Searchable professional profile" },
                { title: "Stand out in a competitive market" },
            ],
        },
    },
    {
        slug: "global-innovation-green-showcasing",
        menuIconSrc: iconSolGreen,
        menuLabel: "Global Innovation & Green Showcasing",
        menuDesc:
            "The Global Innovation and Green Showcasing feature allows you to publish projects and highlight their sustainable impact. It empowers teams to build compelling digital portfolios.",
        menuIcon: FaLeaf,
        featured: true,
        overtitle: "Global Innovation & Green Showcasing",
        title: "Showcase Innovation.",
        titleHighlight: "Champion Sustainability",
        subtitle:
            "Publish projects and highlight their sustainable impact. ConRa empowers teams to build compelling digital portfolios that put green building and innovation on the global stage.",
        benefitsIntro:
            "Verified sustainability claims turn green ambition into trusted, discoverable proof.",
        benefits: [
            { icon: FaSeedling, title: "Verified Green Impact", desc: "Back every sustainability claim with tamper-proof site evidence." },
            { icon: FaAward, title: "Innovation Spotlight", desc: "Showcase pioneering methods, materials and designs to a global audience." },
            { icon: FaHandshake, title: "Attract Green Funding", desc: "Give climate-focused investors and lenders verifiable impact data." },
            { icon: FaChartLine, title: "Measurable Progress", desc: "Track and publish sustainability milestones as your project evolves." },
        ],
        detail: {
            title: "Put Your Impact on the",
            titleHighlight: "Global Stage",
            paragraph:
                "Every green milestone is published with verified evidence — so clients, investors and communities can trust the impact you claim.",
            checklist: [
                { title: "Sustainability milestones with verified evidence" },
                { title: "Discoverable in the Green Building Directory" },
                { title: "Compelling, shareable digital portfolios" },
                { title: "Trusted by investors and institutions" },
            ],
        },
    },
];

const FEATURE_SLUGS = new Set([
    "c2pa-certified-media",
    "geofenced-timestamped-audits",
    "tamper-evident-timelines",
    "search-verification-engine",
    "verified-digital-portfolio",
    "global-innovation-green-showcasing",
]);

export const getSolution = (slug) => SOLUTIONS.find((s) => s.slug === slug);

// Features tab (published nav) — the old solution items
export const MENU_FEATURES = SOLUTIONS.filter((s) => FEATURE_SLUGS.has(s.slug) && !s.featured);
export const FEATURED_FEATURE = SOLUTIONS.find((s) => s.featured);

// Solutions tab (published nav) — Banks, NGOs, Diaspora, Contractors, Borderless, Green Building
export const MENU_SOLUTIONS = [
    { slug: "borderless", menuLabel: "Borderless Architecture & Engineering", menuIcon: FaDraftingCompass, menuDesc: "Engineer without limit." },
    { slug: "contractors", menuLabel: "Contractors", menuIcon: FaProjectDiagram, menuDesc: "Build your verified track record." },
    { slug: "diaspora", menuLabel: "Diaspora", menuIcon: FaPlane, menuDesc: "Watch your investment, no flight required." },
    { slug: "banks", menuLabel: "Financial institutions", menuIcon: FaUniversity, menuDesc: "Release funds with confidence." },
    { slug: "green-building", menuLabel: "Green Building", menuIcon: FaSeedling, menuDesc: "Verified on-site proof for green certifications." },
    { slug: "ngos", menuLabel: "NGOs", menuIcon: FaHandshake, menuDesc: "Verify impact from anywhere." },
];
